import { IModel, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { DataFlowJoinType, getJoinTypeString } from 'app/model/dataflow/dataflow-jointype';
import { format } from 'app/shared/utils/string.util';
import { BaseMultiInputStreamTransformation, BroadcastType } from './base-multi-input-stream-transformation';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { ConditionExpression } from 'app/model/dataflow/condition-expression';
import { IDataFlowArguments, convertColumnRefToDataFlowColumn, convertParametersToArguments, convertCacheSinksToArguments } from './dataflow-expression-utilities';
import { DataFlowExpressionFunctions } from './dataflow-expression-functions';
import { updateIdentifiers, asyncUpdateIdentifiers } from './dataflow-expression-update-identifiers';
import { GlobalVariables } from 'app/services/globals';
import { DataFlowJoinMatchType } from './dataflow-joinmatchtype';
import { DataFlowColumn } from '../dataset/dataflowcolumn';
import { Owned } from 'shell-models-lib';
import { SchemaConvertor } from '../adapters/dataflow/adapterVisitor/convertors/schema.convertor';
import { ColumnType } from 'worker/app-workers/shared/plainObjects/column.plainObject';

@Metamodel.model({
    name: 'join',
    display: [stringResources.DataFlowJoinTransformationLabel, stringResources.DataFlowJoinTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        }
    },
    validator: JoinTransformation.prototype.validateJoinTransform
})
export class JoinTransformation extends BaseMultiInputStreamTransformation {
    public static readonly MODEL: IModel;

    /**
     * Property to distinguish either join or anti-join, default value is false.
     */
    public negate?: boolean;

    /**
     * Default value is inner.
     */
    public joinType?: DataFlowJoinType = 'inner';
    public unusedStream: string;

    public matchType?: DataFlowJoinMatchType = 'exact';

    public get usesConditionExpressions(): boolean {
        return this.joinType !== 'cross';
    }

    public ignoreSpaces?: boolean = false;

    public scoreColumn?: string = undefined;

    @Metamodel.attribute({
        display: stringResources.Expression
    })
    public crossExpression: DataFlowExpression;

    public validateJoinTransform(): IViolation[] | undefined {
        const violations = this.inputStreamValidate() || [];

        if (this.joinType === 'cross') {
            if (!this.crossExpression || !this.crossExpression.expression) {
                violations.push({
                    context: this,
                    message: () => stringResources.JoinNoCrossConditionDescription
                });
            } else if (!this.crossExpression.returnsCorrectType) {
                violations.push({
                    context: this,
                    message: () => stringResources.DataFlow_ShouldReturnBoolean
                });
            }
        }
        if (this.matchType === 'fuzzy') {
            if (this.broadcastType !== BroadcastType.OFF) {
                violations.push({
                    context: this,
                    message: () => stringResources.FuzzyJoinBroadcastDescription
                });
            }
            this.conditionExpressions.forEach((condition) => {
                if (condition.leftExpression.returnType.typeString !== 'string' || condition.rightExpression.returnType.typeString !== 'string') {
                    violations.push({
                        context: this,
                        message: () => stringResources.FuzzyJoinStringReturnTypeDescription
                    });
                }
            });
            if (this.joinType !== 'inner' && this.joinType !== 'left' && this.joinType !== 'outer') {
                violations.push({
                    context: this,
                    message: () => stringResources.FuzzyJoinInfo
                });
            }
        }
        return violations;
    }

    public getExpressions(): DataFlowExpression[] {
        if (this.joinType === 'cross') {
            return [this.crossExpression];
        }

        return super.getExpressions();
    }

    public updateInputTransformationCacheInternal(): void {
        if (this.joinType === 'cross') {
            const availableArguments: IDataFlowArguments = {
                dataFlowColumns: convertColumnRefToDataFlowColumn(this.distinctInputColumnRefs),
                dataFlowFunctions: DataFlowExpressionFunctions.ALL_FUNCTIONS,
                dataFlowParameters: convertParametersToArguments(this.dataflow.parameters),
                dataFlowTransientColumns: convertColumnRefToDataFlowColumn(this.transientColumnRefs),
                dataFlowCacheSinks: convertCacheSinksToArguments(this.dataflow.cacheSinks)
            };
            if (GlobalVariables.validateDataFlowAPI) {
                asyncUpdateIdentifiers(availableArguments, this.crossExpression, this);
            } else {
                updateIdentifiers(availableArguments, this.crossExpression);
            }
        } else {
            super.updateInputTransformationCacheInternal();
        }
        this.checkCrossJoinCondition();
    }

    protected _outputColumns(): DataFlowColumn[] {
        const columns: DataFlowColumn[] = [];
        for (const column of this.inputColumns) {
            columns.push(column);
        }
        if (this.matchType === 'fuzzy' && this.scoreColumn) {
            const col = Owned.create<DataFlowColumn>(DataFlowColumn.MODEL, this);
            col.dataFlowColumnType = SchemaConvertor.convertDataType(new ColumnType('float'));
            col.name = this.scoreColumn;
            columns.push(col);
        }
        return columns;

    }

    protected getGeneratedDescription(): string {
        if (this.joinType && this.inputStreams.length === 2) {
            return format(stringResources.JoinDescription, getJoinTypeString(this.joinType), this.inputStreams[0].qualifiedName, this.inputStreams[1].qualifiedName);
        }
        return stringResources.JoinNoSecondStreamDescription;
    }

    protected getConditionExpressions(): ConditionExpression[] {
        if (this.joinType === 'cross') {
            return [];
        }

        return super.getConditionExpressions();
    }

    private checkCrossJoinCondition(): void {
        this.unusedStream = '';
        if (this.joinType === 'cross' && this.inputStreams.length === 2 && this.crossExpression) {
            let leftStreamCount = 0;
            let rightStreamCount = 0;
            this.crossExpression.argumentsUsed.dataFlowColumns.forEach((columnArgument) => {
                if (columnArgument.column.inputStreamName === this.inputStreams[0].qualifiedName) {
                    leftStreamCount++;
                } else if (columnArgument.column.inputStreamName === this.inputStreams[1].qualifiedName) {
                    rightStreamCount++;
                }
            });
            this.unusedStream = !leftStreamCount ? this.inputStreams[0].qualifiedName : !rightStreamCount ? this.inputStreams[1].qualifiedName : '';
        }
    }

}
